interface Props {
  followupChecked: boolean
  patternChecked: boolean
  onFollowupChange: (v: boolean) => void
  onPatternChange: (v: boolean) => void
  onReceiptCopied?: () => void
}

export function ConsentExplainer({ 
  followupChecked, 
  patternChecked, 
  onFollowupChange, 
  onPatternChange,
  onReceiptCopied 
}: Props) {
  const followupText = `By checking this, you ask the system to prepare a private re-weighing page that will become available 14 days after closing.

How this works with no notification abuse:
• No email, push notification, SMS, browser notification, or in-app message will be sent to you by this system at any time.
• You will be shown a private check-in URL immediately after closing. You are responsible for saving it (bookmark, notes app, print on the card, or add a calendar entry using the generated .ics).
• A Cloudflare Workflow will execute after the delay and pre-generate a short reflection packet (three precise questions + suggested evidence re-weigh) using only the data in this ledger and any consented aggregate patterns.
• The packet is stored under a token that only the URL reveals. If you never visit, nothing further happens and no compute is wasted on your behalf.
• Visiting the URL after the scheduled date shows the prepared packet + space to record what actually occurred. You may then close a small follow-up ledger if you wish.
• You can delete the entire ledger (including any prepared follow-up) at any time using your private control link.
• This mechanism exists so that future-you has a low-friction, pre-loaded place to weigh evidence again without relying on memory or external reminders.`

  const patternText = `Contribute an anonymized embedding of the structure of this ledger (number of stated values, evidence support levels, boundary types, presence of explicit "not doing" commitment) to a shared Vectorize index. No raw text or identifiable content is ever stored. The only use is to let other users see coarse statistical observations such as "ledgers with 3+ stated values and mostly 'anecdotal' evidence later recorded scope reductions in the majority of followed-up cases." Deleting this ledger removes your contribution from future queries.`

  return (
    <div className="space-y-6">
      <div className="consent-box rounded-lg p-5 text-[15px] leading-relaxed text-ink-muted">
        <div className="font-medium text-ink mb-3 text-sm tracking-wide">DELAYED FOLLOW-UP CONSENT — READ CAREFULLY</div>
        <div className="whitespace-pre-line text-[14.5px]">{followupText}</div>
      </div>

      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={followupChecked}
          onChange={(e) => onFollowupChange(e.target.checked)}
          className="mt-1 h-4 w-4 accent-[#3a4f41] border-[#c9c6bb]"
        />
        <div className="text-[15px] leading-tight">
          I want a private 14-day re-weighing page prepared for me. I understand that <span className="font-medium text-ink">no notification of any kind will ever be sent</span>. I am responsible for saving the check-in URL shown after closing.
        </div>
      </label>

      <div className="consent-box rounded-lg p-5 text-[15px] leading-relaxed text-ink-muted mt-2">
        <div className="font-medium text-ink mb-3 text-sm tracking-wide">OPTIONAL PATTERN CONTRIBUTION</div>
        <div className="text-[14.5px]">{patternText}</div>
      </div>

      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={patternChecked}
          onChange={(e) => onPatternChange(e.target.checked)}
          className="mt-1 h-4 w-4 accent-[#3a4f41] border-[#c9c6bb]"
        />
        <div className="text-[15px] leading-tight">
          (optional) Contribute only the structural shape of this ledger to the shared pattern index.
        </div>
      </label>

      <div className="pt-2 text-xs text-ink-light">
        A consent receipt (exact text + timestamp + your choices) will be attached to your private control link and included in all exports. You can copy it below after closing.
      </div>
    </div>
  )
}
